import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/brand/StatusBadge";
import AppointmentDialog from "@/components/appointments/AppointmentDialog";
import { statusTone } from "@/lib/appointments";
import { CalendarDays, Loader2, Plus, Pencil, Clock, MapPin, Target, Zap, MessageCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

export default function AppointmentsPanel({ customerId, customerName, onChanged, presetOpportunityId }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = !!user?.can_manage_appointments;
  const [rows, setRows] = useState(null);
  const [add, setAdd] = useState(false);
  const [edit, setEdit] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/customers/${customerId}/appointments`); setRows(data); }
    catch (e) { setRows([]); }
  }, [customerId]);
  useEffect(() => { load(); }, [load]);
  const refresh = () => { load(); onChanged?.(); };

  return (
    <div className="space-y-4" data-testid="appointments-panel">
      <div className="flex items-center justify-between">
        <h3 className="ds-section-title flex items-center gap-2"><CalendarDays className="h-4 w-4 text-brand" /> Appuntamenti</h3>
        {canManage && <Button size="sm" onClick={() => setAdd(true)} className="gap-1.5" data-testid="add-appointment-button"><Plus className="h-4 w-4" /> Nuovo appuntamento</Button>}
      </div>
      {rows === null ? (
        <div className="py-10 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-brand" /></div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 py-12 text-center text-slate-500">
          <CalendarDays className="h-8 w-8 text-slate-300" /><div className="font-display font-semibold text-slate-700">Nessun appuntamento</div>
          <p className="max-w-sm text-sm">Pianifica un sopralluogo o una visita per questo cliente.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((a) => (
            <div key={a.id} className="ds-card flex flex-wrap items-center justify-between gap-3 p-3" data-testid={`appt-item-${a.id}`}>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-slate-800">{new Date(a.appt_date).toLocaleDateString("it-IT")}</span>
                  {a.appt_time && <span className="inline-flex items-center gap-1 text-sm text-slate-500"><Clock className="h-3.5 w-3.5" /> {a.appt_time}{a.end_time ? `–${a.end_time}` : ""}</span>}
                  {a.duration_min && <span className="text-xs text-slate-400">({a.duration_min} min)</span>}
                  {a.type && <span className="rounded-full bg-brand/10 px-2 py-0.5 text-xs font-semibold text-brand">{a.type}</span>}
                  <StatusBadge type={statusTone(a.status)}>{a.status}</StatusBadge>
                </div>
                <div className="mt-0.5 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-slate-500">
                  {a.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {a.location}</span>}
                  {a.address && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {a.address}</span>}
                  {a.opportunity_product && <span className="inline-flex items-center gap-1"><Target className="h-3 w-3" /> {a.opportunity_product}</span>}
                  {a.supply_label && <span className="inline-flex items-center gap-1"><Zap className="h-3 w-3" /> {a.supply_label}</span>}
                  {a.outcome && <span className="text-success">Esito: {a.outcome}</span>}
                </div>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => navigate(`/whatsapp?customer=${customerId}&appointment=${a.id}&template=${encodeURIComponent("Conferma appuntamento")}`)} title="Conferma via WhatsApp" data-testid={`appt-whatsapp-confirm-${a.id}`}><MessageCircle className="h-4 w-4 text-success" /></Button>
                <Button size="sm" variant="ghost" onClick={() => navigate(`/whatsapp?customer=${customerId}&appointment=${a.id}&template=${encodeURIComponent("Promemoria appuntamento")}`)} title="Promemoria via WhatsApp" data-testid={`appt-whatsapp-remind-${a.id}`}><MessageCircle className="h-4 w-4 text-brand" /></Button>
                {canManage && <Button size="sm" variant="ghost" onClick={() => setEdit(a)} data-testid={`edit-appt-${a.id}`}><Pencil className="h-4 w-4" /></Button>}
              </div>
            </div>
          ))}
        </div>
      )}
      <AppointmentDialog open={add} onOpenChange={setAdd} customerId={customerId} customerName={customerName} presetOpportunityId={presetOpportunityId} onSaved={refresh} />
      {edit && <AppointmentDialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)} customerId={customerId} customerName={customerName} appointment={edit} onSaved={() => { setEdit(null); refresh(); }} />}
    </div>
  );
}
